import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, Info } from "lucide-react";
import { API } from "@/lib/api";

const FIELDS = [
  { key: "labor_rate_per_hour", label: "Tarif main-d'œuvre", unit: "€ / heure", step: "1", hint: "Ton taux horaire de travail." },
  { key: "travel_rate_per_km", label: "Frais de déplacement", unit: "€ / km", step: "0.1", hint: "Compté sur l'aller-retour en mode « à domicile »." },
  { key: "minimum_intervention", label: "Minimum d'intervention", unit: "€", step: "1", hint: "Montant plancher d'une intervention." },
];

const GridFields = ({ grid, onChange }) => (
  <div className="mt-8 grid sm:grid-cols-3 gap-4">
    {FIELDS.map((f) => (
      <div key={f.key} className="glass border border-line rounded-2xl p-4">
        <label className="font-mono-ui text-[11px] uppercase text-slate-500">{f.label}</label>
        <div className="mt-2 flex items-center gap-2">
          <input type="number" min="0" step={f.step} value={grid[f.key] ?? ""} onChange={(e) => onChange(f.key, e.target.value)}
            className="w-full bg-ink border border-line rounded-xl px-3 py-2.5 text-lg text-slate-100 focus:border-cyan-400 outline-none" data-testid={`admin-pricing-${f.key}`} />
          <span className="text-xs text-slate-500 whitespace-nowrap">{f.unit}</span>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">{f.hint}</p>
      </div>
    ))}
  </div>
);

export const PricingPanel = () => {
  const [grid, setGrid] = useState({ labor_rate_per_hour: "", travel_rate_per_km: "", minimum_intervention: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setField = (key, value) => setGrid((g) => ({ ...g, [key]: value }));

  useEffect(() => {
    let active = true;
    axios.get(`${API}/pricing-grid`, { withCredentials: true })
      .then(({ data }) => { if (active) setGrid({ ...data }); })
      .catch(() => toast.error("Chargement des tarifs impossible"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        labor_rate_per_hour: Number(grid.labor_rate_per_hour) || 0,
        travel_rate_per_km: Number(grid.travel_rate_per_km) || 0,
        minimum_intervention: Number(grid.minimum_intervention) || 0,
        notes: grid.notes || "",
      };
      const { data } = await axios.put(`${API}/pricing-grid`, payload, { withCredentials: true });
      setGrid({ ...data });
      toast.success("Grille tarifaire enregistrée");
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="py-8 text-slate-400" data-testid="admin-pricing-loading">Chargement…</p>;

  return (
    <div data-testid="admin-pricing-panel">
      <h1 className="font-display uppercase text-4xl">Grille tarifaire</h1>
      <p className="text-xs text-slate-500 mt-1">Ces tarifs servent à calculer les pré-devis de l'assistant. Rien n'est public.</p>

      <p className="mt-5 text-[13px] text-cyan-200/90 border border-cyan-400/30 bg-cyan-400/5 rounded-xl px-4 py-3 flex gap-2" data-testid="admin-pricing-info">
        <Info size={16} className="shrink-0 mt-0.5" />
        L'assistant estime la durée et les pièces, mais n'invente jamais de prix. Les montants viennent uniquement de cette grille ; les pièces restent à chiffrer par toi.
      </p>

      <GridFields grid={grid} onChange={setField} />

      <div className="mt-4 glass border border-line rounded-2xl p-4">
        <label className="font-mono-ui text-[11px] uppercase text-slate-500">Note pour l'assistant (optionnel)</label>
        <textarea rows={3} value={grid.notes ?? ""} onChange={(e) => setField("notes", e.target.value)}
          placeholder="ex : forfait diagnostic 20€, pièces facturées en plus…"
          className="mt-2 w-full bg-ink border border-line rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:border-cyan-400 outline-none resize-none" data-testid="admin-pricing-notes" />
      </div>

      <button type="button" onClick={save} disabled={saving} data-testid="admin-pricing-save"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 text-ink font-bold uppercase text-sm hover:scale-[1.02] transition-transform disabled:opacity-50">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Enregistrer
      </button>
    </div>
  );
};
